<template>
  <div class="modal fade add-plot-modal" tabindex="-1" role="dialog" aria-labelledby="addPlotModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header bg-success text-white">
          <h5 class="modal-title text-uppercase fw-bold text-white" id="addPlotModalLabel">
            <i class="mdi mdi-store-plus-outline me-2"></i>Add New Plot
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Progress Indicator -->
          <div class="bg-light border-bottom p-3">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <div class="step-indicator me-3" :class="{ 'active': currentStep >= 1, 'completed': currentStep > 1 }">
                  <i class="mdi mdi-map-marker"></i>
                </div>
                <div class="step-indicator me-3" :class="{ 'active': currentStep >= 2, 'completed': currentStep > 2 }">
                  <i class="mdi mdi-account"></i>
                </div>
                <div class="step-indicator" :class="{ 'active': currentStep >= 3, 'completed': currentStep > 3 }">
                  <i class="mdi mdi-currency-usd"></i>
                </div>
              </div>
              <small class="text-muted">Step {{ currentStep }} of 3</small>
            </div>
          </div>

          <!-- Tabbed Content -->
          <ul class="nav nav-tabs nav-justified" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                  class="nav-link fw-semibold"
                  :class="{ 'active': currentStep === 1 }"
                  @click="currentStep = 1"
                  type="button">
                <i class="mdi mdi-map-marker-outline me-1"></i>Plot Details
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                  class="nav-link fw-semibold"
                  :class="{ 'active': currentStep === 2 }"
                  @click="currentStep = 2"
                  type="button">
                <i class="mdi mdi-account-outline me-1"></i>Owner Details
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                  class="nav-link fw-semibold"
                  :class="{ 'active': currentStep === 3 }"
                  @click="currentStep = 3"
                  type="button">
                <i class="mdi mdi-currency-usd me-1"></i>Financial Details
              </button>
            </li>
          </ul>

          <div class="tab-content p-4">
            <!-- Step 1: Plot Details -->
            <div v-show="currentStep === 1" class="step-content">
              <div class="row">
                <div class="col-12 mb-4">
                  <h6 class="fw-bold text-success mb-3">
                    <i class="mdi mdi-information-outline me-2"></i>Basic Plot Information
                  </h6>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Plot Number <span class="text-danger">*</span>
                    </label>
                    <input
                        v-model="localPlot.plotNumber"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.plotNumber }"
                        placeholder="Enter plot number (e.g., ST-001)">
                    <div v-if="errors.plotNumber" class="invalid-feedback">{{ errors.plotNumber }}</div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Zone/Market <span class="text-danger">*</span>
                    </label>
                    <select
                        v-model="localPlot.zone"
                        class="form-select"
                        :class="{ 'is-invalid': errors.zone }">
                      <option value="">Select Zone/Market</option>
                      <option v-for="zone in zones" :key="zone.zone" :value="zone.zone">
                        {{ zone.zone }}
                      </option>
                    </select>
                    <div v-if="errors.zone" class="invalid-feedback">{{ errors.zone }}</div>
                    <small class="text-muted">Or enter new zone:</small>
                    <input
                        v-model="newZone"
                        type="text"
                        class="form-control mt-1"
                        placeholder="Enter new zone name"
                        @input="handleNewZone">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Sub County <span class="text-danger">*</span>
                    </label>
                    <select
                        class="form-select"
                        :class="{ 'is-invalid': errors.subCounty }"
                        @change="handleSubCountyChange"
                        v-model="selectedSubCounty">
                      <option value="">Select Sub County</option>
                      <option v-for="(item, index) in subCounties" :key="index" :value="item">
                        {{ item.subCountyName }}
                      </option>
                    </select>
                    <div v-if="errors.subCounty" class="invalid-feedback">{{ errors.subCounty }}</div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Ward</label>
                    <select class="form-select" v-model="selectedWard">
                      <option value="">Select Ward</option>
                      <option v-for="(item, index) in wards" :key="index" :value="item">
                        {{ item.wardName }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Plot Size</label>
                    <input
                        v-model="localPlot.plotSize"
                        type="text"
                        class="form-control"
                        placeholder="e.g., 3x3m, 10sqm">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Status</label>
                    <select class="form-select" v-model="localPlot.status">
                      <option value="Active">Active</option>
                      <option value="Vacant">Vacant</option>
                      <option value="Suspended">Suspended</option>
                    </select>
                  </div>
                </div>

                <div class="col-12">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Plot Address/Location</label>
                    <textarea
                        v-model="localPlot.plotAddress"
                        class="form-control"
                        rows="2"
                        placeholder="Enter plot location details"></textarea>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Latitude</label>
                    <input
                        v-model="localPlot.lat"
                        type="text"
                        class="form-control"
                        placeholder="e.g., -1.2921">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Longitude</label>
                    <input
                        v-model="localPlot.lng"
                        type="text"
                        class="form-control"
                        placeholder="e.g., 36.8219">
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Owner Details -->
            <div v-show="currentStep === 2" class="step-content">
              <div class="row">
                <div class="col-12 mb-4">
                  <h6 class="fw-bold text-success mb-3">
                    <i class="mdi mdi-account-outline me-2"></i>Owner Information
                  </h6>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">
                      Owner Name <span class="text-danger">*</span>
                    </label>
                    <input
                        v-model="localPlot.ownerNames"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.ownerNames }"
                        placeholder="Enter owner's full name">
                    <div v-if="errors.ownerNames" class="invalid-feedback">{{ errors.ownerNames }}</div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">ID Number</label>
                    <input
                        v-model="localPlot.ownerID"
                        type="text"
                        class="form-control"
                        placeholder="Enter ID number">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Phone Number</label>
                    <input
                        v-model="localPlot.ownerPhoneNo"
                        type="tel"
                        class="form-control"
                        placeholder="e.g., 0712345678">
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label fw-semibold">Email Address</label>
                    <input
                        v-model="localPlot.ownerEmail"
                        type="email"
                        class="form-control"
                        placeholder="Enter email address">
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Financial Details -->
            <div v-show="currentStep === 3" class="step-content">
              <div class="row">
                <div class="col-12 mb-4">
                  <h6 class="fw-bold text-success mb-3">
                    <i class="mdi mdi-currency-usd me-2"></i>Financial Information
                  </h6>
                </div>

                <!-- Annual Rent -->
                <div class="col-12 mb-4">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-success text-white">
                      <h6 class="mb-0 fw-bold text-white">
                        <i class="mdi mdi-calendar-clock me-2"></i>Annual Rent
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Annual Rent (KES) <span class="text-danger">*</span></label>
                            <input
                                v-model.number="localPlot.annualRent"
                                type="number"
                                step="0.01"
                                class="form-control"
                                :class="{ 'is-invalid': errors.annualRent }"
                                placeholder="0.00"
                                @input="calculateTotals">
                            <div v-if="errors.annualRent" class="invalid-feedback">{{ errors.annualRent }}</div>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Last Billing Year</label>
                            <select v-model="localPlot.lastBillingYear" class="form-select">
                              <option value="">Not Yet Billed</option>
                              <option :value="currentYear">{{ currentYear }}</option>
                              <option :value="currentYear - 1">{{ currentYear - 1 }}</option>
                              <option :value="currentYear - 2">{{ currentYear - 2 }}</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Arrears & Penalties -->
                <div class="col-12 mb-4">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-danger text-white">
                      <h6 class="mb-0 fw-bold text-white">
                        <i class="mdi mdi-alert-circle me-2"></i>Arrears Brought Forward
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Arrears B/F (KES)</label>
                            <input
                                v-model.number="localPlot.arrearsForward"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                            <small class="text-muted">Outstanding balance from previous years</small>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Accumulated Penalty (KES)</label>
                            <input
                                v-model.number="localPlot.accumulatedPenalty"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                @input="calculateTotals">
                            <small class="text-muted">25% penalty on unpaid arrears</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Summary -->
                <div class="col-12 mb-4">
                  <div class="card border-0 shadow-sm">
                    <div class="card-header bg-primary text-white">
                      <h6 class="mb-0 fw-bold text-white">
                        <i class="mdi mdi-calculator me-2"></i>Balance Summary
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Total Payments Made (KES)</label>
                            <input
                                v-model.number="localPlot.totalPayments"
                                type="number"
                                step="0.01"
                                class="form-control"
                                placeholder="0.00"
                                readonly>
                            <small class="text-muted">Set to 0 for new plots</small>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="form-group mb-3">
                            <label class="form-label fw-semibold">Current Balance (KES)</label>
                            <input
                                v-model.number="localPlot.currentBalance"
                                type="number"
                                step="0.01"
                                class="form-control bg-light"
                                :class="localPlot.currentBalance > 0 ? 'border-danger text-danger' : 'border-success text-success'"
                                placeholder="0.00"
                                readonly>
                            <small class="text-muted">Auto-calculated: Arrears + Penalty</small>
                          </div>
                        </div>
                      </div>

                      <!-- Balance Summary Alert -->
                      <div class="alert alert-info mt-3">
                        <h6 class="fw-bold mb-2">
                          <i class="mdi mdi-information-outline me-2"></i>Balance Calculation
                        </h6>
                        <div class="row text-center">
                          <div class="col-md-4">
                            <small class="text-muted d-block">Arrears B/F</small>
                            <strong class="text-danger">KES {{ numberFormat(localPlot.arrearsForward || 0) }}</strong>
                          </div>
                          <div class="col-md-4">
                            <small class="text-muted d-block">Penalty</small>
                            <strong class="text-warning">KES {{ numberFormat(localPlot.accumulatedPenalty || 0) }}</strong>
                          </div>
                          <div class="col-md-4">
                            <small class="text-muted d-block">Current Balance</small>
                            <strong :class="localPlot.currentBalance > 0 ? 'text-danger' : 'text-success'">
                              KES {{ numberFormat(localPlot.currentBalance || 0) }}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button
                  v-if="currentStep > 1"
                  type="button"
                  class="btn btn-secondary"
                  @click="currentStep--">
                <i class="mdi mdi-arrow-left me-1"></i>Previous
              </button>
            </div>

            <div>
              <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">
                <i class="mdi mdi-close me-1"></i>Cancel
              </button>

              <button
                  v-if="currentStep < 3"
                  type="button"
                  class="btn btn-primary"
                  @click="nextStep">
                Next<i class="mdi mdi-arrow-right ms-1"></i>
              </button>

              <button
                  v-if="currentStep === 3"
                  type="button"
                  class="btn btn-success"
                  @click="handleSubmit"
                  :disabled="!isFormValid">
                <i class="mdi mdi-check me-1"></i>Add Plot
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddPlotModal',
  props: {
    subCounties: {
      type: Array,
      default: () => []
    },
    wards: {
      type: Array,
      default: () => []
    },
    zones: {
      type: Array,
      default: () => []
    }
  },
// In the emits array, add 'get-zones'
  emits: ['submit', 'get-wards', 'get-zones'],
  data() {
    return {
      currentStep: 1,
      currentYear: new Date().getFullYear(),
      errors: {},
      newZone: '',
      localPlot: {
        plotNumber: '',
        zone: '',
        subCountyID: '',
        subCountyName: '',
        wardID: '',
        wardName: '',
        ownerNames: '',
        ownerID: '',
        ownerPhoneNo: '',
        ownerEmail: '',
        annualRent: 0,
        arrearsForward: 0,
        accumulatedPenalty: 0,
        totalBilled: 0,
        totalPayments: 0,
        currentBalance: 0,
        plotSize: '',
        plotAddress: '',
        lat: '',
        lng: '',
        status: 'Active',
        lastBillingYear: ''
      },
      selectedSubCounty: null,
      selectedWard: null
    }
  },
  computed: {
    isFormValid() {
      return this.localPlot.plotNumber &&
          this.localPlot.zone &&
          this.selectedSubCounty &&
          this.localPlot.ownerNames &&
          this.localPlot.annualRent > 0;
    }
  },
  methods: {
    nextStep() {
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    },

    validateCurrentStep() {
      this.errors = {};

      if (this.currentStep === 1) {
        if (!this.localPlot.plotNumber) {
          this.errors.plotNumber = 'Plot number is required';
        }
        if (!this.localPlot.zone) {
          this.errors.zone = 'Zone/Market is required';
        }
        if (!this.selectedSubCounty) {
          this.errors.subCounty = 'Sub County is required';
        }
      }

      if (this.currentStep === 2) {
        if (!this.localPlot.ownerNames) {
          this.errors.ownerNames = 'Owner name is required';
        }
      }

      if (this.currentStep === 3) {
        if (!this.localPlot.annualRent || this.localPlot.annualRent <= 0) {
          this.errors.annualRent = 'Annual rent is required and must be greater than 0';
        }
      }

      return Object.keys(this.errors).length === 0;
    },

    handleNewZone() {
      if (this.newZone.trim()) {
        this.localPlot.zone = this.newZone.trim().toUpperCase();
      }
    },

    handleSubCountyChange() {
      if (this.selectedSubCounty) {
        this.localPlot.subCountyID = this.selectedSubCounty.subCountyID;
        this.localPlot.subCountyName = this.selectedSubCounty.subCountyName;
        this.$emit('get-wards', this.selectedSubCounty.subCountyID);
        this.$emit('get-zones', this.selectedSubCounty.subCountyID);  // Add this line
      }
      this.selectedWard = null;
      this.localPlot.zone = '';  // Reset zone when sub county changes
    },

    calculateTotals() {
      // Current Balance = Arrears B/F + Accumulated Penalty - Total Payments
      this.localPlot.currentBalance =
          (parseFloat(this.localPlot.arrearsForward) || 0) +
          (parseFloat(this.localPlot.accumulatedPenalty) || 0) -
          (parseFloat(this.localPlot.totalPayments) || 0);
    },

    handleSubmit() {
      if (!this.validateCurrentStep()) {
        return;
      }

      // Ensure calculations are up to date
      this.calculateTotals();

      // Set ward details
      if (this.selectedWard) {
        this.localPlot.wardID = this.selectedWard.wardID;
        this.localPlot.wardName = this.selectedWard.wardName;
      }

      this.$emit('submit', { ...this.localPlot });

      // Reset form
      this.resetForm();
    },

    resetForm() {
      this.currentStep = 1;
      this.errors = {};
      this.newZone = '';
      this.selectedSubCounty = null;
      this.selectedWard = null;
      this.localPlot = {
        plotNumber: '',
        zone: '',
        subCountyID: '',
        subCountyName: '',
        wardID: '',
        wardName: '',
        ownerNames: '',
        ownerID: '',
        ownerPhoneNo: '',
        ownerEmail: '',
        annualRent: 0,
        arrearsForward: 0,
        accumulatedPenalty: 0,
        totalBilled: 0,
        totalPayments: 0,
        currentBalance: 0,
        plotSize: '',
        plotAddress: '',
        lat: '',
        lng: '',
        status: 'Active',
        lastBillingYear: ''
      };
    },

    numberFormat(num) {
      const formatter = new Intl.NumberFormat("en-GB");
      return formatter.format(num || 0);
    }
  }
}
</script>

<style scoped>
.step-indicator {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.3s ease;
}

.step-indicator.active {
  background-color: #28a745;
  color: white;
}

.step-indicator.completed {
  background-color: #198754;
  color: white;
}

.nav-tabs .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  color: #6c757d;
}

.nav-tabs .nav-link:hover {
  border-color: transparent;
  color: #495057;
  background-color: #f8f9fa;
}

.nav-tabs .nav-link.active {
  color: #28a745;
  background-color: transparent;
  border-color: transparent transparent #28a745;
  font-weight: 600;
}

.form-label {
  color: #495057;
  margin-bottom: 0.5rem;
}

.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
}

.step-content {
  min-height: 350px;
}
</style>